/* Published Projects for Advisors — logged-in project board with masked/unmasked
   privacy, advisor "express interest" replies (full profile shared), owner reply
   threads, and demo-mode Premium via the existing Razorpay integration. */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "./PublishedProjects.css";

const API = process.env.REACT_APP_BACKEND_URL;

const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
const timeAgo = (iso) => {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

const loadScript = (src) =>
  new Promise((res) => {
    if (document.querySelector(`script[src="${src}"]`)) return res(true);
    const s = document.createElement("script");
    s.src = src; s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });

/* ── Premium (Razorpay) modal ────────────────────────────────────────── */
const PremiumModal = ({ open, onClose, me, onActivated }) => {
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const payWithRazorpay = async () => {
    setBusy(true);
    try {
      const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!ok) { toast.error("Could not load Razorpay. Please try again."); setBusy(false); return; }
      const cfg = await (await fetch(`${API}/api/payments/config`)).json();
      const orderRes = await fetch(`${API}/api/payments/create-order`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: "projects_premium",
          customer: {
            name: me?.name || "IPOLabs User", email: me?.email || "user@ipolabs.com",
            phone: "9999999999", address_line1: "India", city: "Mumbai",
            state: "Maharashtra", state_code: "27", pincode: "400001",
          },
        }),
      });
      if (!orderRes.ok) {
        const d = await orderRes.json().catch(() => ({}));
        toast.error(d.detail || "Could not start Razorpay checkout. Please try again.");
        setBusy(false); return;
      }
      const order = await orderRes.json();
      const rzp = new window.Razorpay({
        key: cfg.razorpay_key_id, amount: order.amount_paise, currency: "INR",
        name: "IPOLabs", description: "Projects Premium — unmask full project details",
        order_id: order.order_id,
        prefill: { name: me?.name || "", email: me?.email || "" },
        theme: { color: "#003153" },
        handler: async (resp) => {
          try {
            const vr = await fetch(`${API}/api/payments/verify`, {
              method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }),
            });
            if (!vr.ok) { toast.error("Payment could not be verified. If you were charged, contact support."); setBusy(false); return; }
            // Confirm the entitlement was granted server-side before unlocking.
            const st = await (await fetch(`${API}/api/project-board/premium`, { credentials: "include" })).json();
            if (st?.is_premium) { toast.success("Premium unlocked — full project details are now visible."); onActivated(); }
            else { toast.error("Payment verified but access is still activating — please refresh in a moment."); }
          } catch { toast.error("Something went wrong confirming your payment."); }
          setBusy(false);
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch {
      toast.error("Razorpay could not open. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="pp-ov" onClick={onClose} data-testid="pp-premium-modal">
      <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-h">Become a Premium User</div>
        <div className="pp-modal-sub">Unlock every project in full — budgets, timelines, complete briefs and the project owner's contact details.</div>
        <div className="pp-prem-price">₹1,999 <small>+ GST · one-time</small></div>
        <ul className="pp-prem-list">
          <li><i className="ti ti-check" /> View every project fully unmasked, including contact details</li>
          <li><i className="ti ti-check" /> See budgets, timelines and full project briefs</li>
          <li><i className="ti ti-check" /> Reach out to project owners directly</li>
          <li><i className="ti ti-check" /> Priority visibility for your advisor profile</li>
        </ul>
        <div className="pp-modal-foot" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <button className="pp-btn ghost" disabled={busy} onClick={onClose} data-testid="pp-premium-cancel">Cancel</button>
          <button className="pp-btn" disabled={busy} onClick={payWithRazorpay} data-testid="pp-premium-pay">
            <i className="ti ti-brand-cashapp" /> {busy ? "Please wait…" : "Pay with Razorpay"}
          </button>
        </div>
        <div className="pp-prem-note">Secure payment via Razorpay. Full project details unlock automatically once your payment is verified.</div>
      </div>
    </div>
  );
};

/* ── Post a Project modal ────────────────────────────────────────────── */
const PostModal = ({ open, onClose, categories, onPosted }) => {
  const [f, setF] = useState({ poster_name: "", contact: "", title: "", category: "DRHP Drafting", budget_range: "", timeline: "", short_summary: "", full_details: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  if (!open) return null;

  const submit = async () => {
    if (!f.poster_name.trim() || !f.title.trim() || !f.short_summary.trim()) { toast.error("Please add your name, project title and a short summary."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/project-board/projects`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(f),
      });
      if (r.ok) { toast.success("Project posted! Advisors can now respond."); onPosted(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Could not post project."); }
    } catch { toast.error("Could not post project. Please ensure you're signed in."); }
    setBusy(false);
  };

  return (
    <div className="pp-ov" onClick={onClose} data-testid="pp-post-modal">
      <div className="pp-modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-h">Post a project</div>
        <div className="pp-modal-sub">Only your name, category, title and one-line summary are shown publicly. Budget, timeline, full brief and contact stay hidden until an advisor unlocks them.</div>
        <div className="pp-privacy"><i className="ti ti-shield-lock" /> <span>Your contact details are private. They're revealed only to Premium users and to advisors you choose to reply to.</span></div>
        <div className="pp-row2">
          <div className="pp-field"><label className="pp-label">Your name / company</label><input className="pp-input" value={f.poster_name} onChange={set("poster_name")} placeholder="e.g. Rajesh Agarwal" data-testid="pp-post-name" /></div>
          <div className="pp-field"><label className="pp-label">Contact <span className="priv">private</span></label><input className="pp-input" value={f.contact} onChange={set("contact")} placeholder="email / mobile" data-testid="pp-post-contact" /></div>
        </div>
        <div className="pp-field"><label className="pp-label">Project title</label><input className="pp-input" value={f.title} onChange={set("title")} placeholder="e.g. SME IPO — full DRHP drafting" data-testid="pp-post-title" /></div>
        <div className="pp-row2">
          <div className="pp-field"><label className="pp-label">Category</label>
            <select className="pp-select" value={f.category} onChange={set("category")} data-testid="pp-post-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="pp-field"><label className="pp-label">Budget range <span className="priv">hidden</span></label><input className="pp-input" value={f.budget_range} onChange={set("budget_range")} placeholder="e.g. ₹8–12 Lakh" data-testid="pp-post-budget" /></div>
        </div>
        <div className="pp-field"><label className="pp-label">Timeline <span className="priv">hidden</span></label><input className="pp-input" value={f.timeline} onChange={set("timeline")} placeholder="e.g. 4–5 months" data-testid="pp-post-timeline" /></div>
        <div className="pp-field"><label className="pp-label">Short summary (public)</label><textarea className="pp-textarea" value={f.short_summary} onChange={set("short_summary")} placeholder="One line advisors will see — describe the scope." data-testid="pp-post-summary" /></div>
        <div className="pp-field"><label className="pp-label">Full brief <span className="priv">hidden</span></label><textarea className="pp-textarea" value={f.full_details} onChange={set("full_details")} placeholder="The complete requirement — revealed only when unlocked." data-testid="pp-post-details" /></div>
        <div className="pp-modal-foot">
          <button className="pp-btn ghost" onClick={onClose} data-testid="pp-post-cancel">Cancel</button>
          <button className="pp-btn" disabled={busy} onClick={submit} data-testid="pp-post-submit">{busy ? "Posting…" : "Post project →"}</button>
        </div>
      </div>
    </div>
  );
};

/* ── Advisor reply / express-interest modal ──────────────────────────── */
const ReplyModal = ({ project, onClose, onReplied }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  if (!project) return null;
  const submit = async () => {
    if (!msg.trim()) { toast.error("Add a short note about how you can help."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/project-board/projects/${project.project_id}/reply`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      if (r.ok) { toast.success("Interest sent — your full profile has been shared with the project owner."); onReplied(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Could not send your reply."); }
    } catch { toast.error("Could not send your reply."); }
    setBusy(false);
  };
  return (
    <div className="pp-ov" onClick={onClose} data-testid="pp-reply-modal">
      <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-h">Express interest</div>
        <div className="pp-modal-sub">Replying to <b>{project.title}</b></div>
        <div className="pp-privacy"><i className="ti ti-user-check" /> <span>Your entire advisor profile (credentials, expertise, experience &amp; a link to your profile page) will be shared with the project owner along with this note.</span></div>
        <div className="pp-field"><label className="pp-label">Your message</label><textarea className="pp-textarea" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Introduce yourself and how you can help with this project…" data-testid="pp-reply-message" /></div>
        <div className="pp-modal-foot">
          <button className="pp-btn ghost" onClick={onClose} data-testid="pp-reply-cancel">Cancel</button>
          <button className="pp-btn" disabled={busy} onClick={submit} data-testid="pp-reply-submit">{busy ? "Sending…" : "Send interest & unlock →"}</button>
        </div>
      </div>
    </div>
  );
};

/* ── Project card ────────────────────────────────────────────────────── */
const ProjectCard = ({ p, isAdvisor, onReply, onBecomePremium }) => (
  <div className="pp-card" data-testid={`pp-card-${p.project_id}`}>
    <div className="pp-card-head">
      <div className="pp-av">{initials(p.poster_name)}</div>
      <div>
        <div className="pp-poster" data-testid={`pp-poster-${p.project_id}`}>{p.poster_name}</div>
        <div className="pp-posted">Posted {timeAgo(p.created_at)}{p.reply_count ? ` · ${p.reply_count} advisor${p.reply_count !== 1 ? "s" : ""} interested` : ""}</div>
      </div>
    </div>
    <span className="pp-tag">{p.category}</span>
    <div className="pp-title">{p.title}</div>
    <div className="pp-teaser">{p.short_summary}</div>

    {p.unlocked ? (
      <div className="pp-detail" data-testid={`pp-unlocked-${p.project_id}`}>
        {p.budget_range && <div className="pp-drow"><span className="k">Budget</span><span className="v">{p.budget_range}</span></div>}
        {p.timeline && <div className="pp-drow"><span className="k">Timeline</span><span className="v">{p.timeline}</span></div>}
        {p.contact && <div className="pp-drow"><span className="k">Contact</span><span className="v">{p.contact}</span></div>}
        {p.full_details && <div className="pp-full">{p.full_details}</div>}
      </div>
    ) : (
      <div className="pp-masked" data-testid={`pp-masked-${p.project_id}`}>
        <div className="pp-masked-rows">
          <div className="pp-mrow w50" /><div className="pp-mrow w90" /><div className="pp-mrow w70" /><div className="pp-mrow w80" />
        </div>
        <div className="pp-lock">
          <div className="pp-lock-ic"><i className="ti ti-lock" /></div>
          <div className="pp-lock-t">Budget, timeline, full brief &amp; contact are hidden</div>
          <div className="pp-lock-d">Become a Premium User to view the entire project details including contact details.</div>
        </div>
      </div>
    )}

    <div className="pp-card-foot">
      {p.is_owner ? (
        <span className="pp-count"><i className="ti ti-user" /> Your project</span>
      ) : isAdvisor ? (
        p.has_replied ? (
          <span className="pp-replied"><i className="ti ti-circle-check" /> You've responded</span>
        ) : (
          <button className="pp-btn sm" onClick={() => onReply(p)} data-testid={`pp-reply-btn-${p.project_id}`}><i className="ti ti-message-2" /> Reply to unlock</button>
        )
      ) : null}
      {!p.unlocked && (
        <button className="pp-btn sm amber" onClick={onBecomePremium} data-testid={`pp-premium-btn-${p.project_id}`}><i className="ti ti-crown" /> Become Premium</button>
      )}
    </div>
  </div>
);

/* ── Main page ───────────────────────────────────────────────────────── */
const PublishedProjects = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [isAdvisor, setIsAdvisor] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("browse");
  const [showPost, setShowPost] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [replyFor, setReplyFor] = useState(null);
  const [mine, setMine] = useState([]);
  const [myResponses, setMyResponses] = useState([]);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/project-board/projects`, { credentials: "include" });
      const d = await r.json();
      setProjects(d.projects || []);
      setIsPremium(!!d.is_premium);
      setIsAdvisor(!!d.is_advisor);
    } catch { setProjects([]); }
    setLoading(false);
  }, []);

  const loadMine = useCallback(async () => {
    try { const d = await (await fetch(`${API}/api/project-board/mine`, { credentials: "include" })).json(); setMine(d.projects || []); } catch { setMine([]); }
  }, []);
  const loadResponses = useCallback(async () => {
    try { const d = await (await fetch(`${API}/api/project-board/my-interests`, { credentials: "include" })).json(); setMyResponses(d.interests || []); } catch { setMyResponses([]); }
  }, []);

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: "include" }).then((r) => r.ok ? r.json() : null).then(setMe).catch(() => {});
    fetch(`${API}/api/project-board/categories`).then((r) => r.json()).then((d) => setCategories(d.categories || [])).catch(() => {});
    loadProjects();
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("premium") === "1") setPremiumOpen(true);
    if (sp.get("post") === "1") setShowPost(true);
  }, [loadProjects]);

  useEffect(() => { if (view === "mine") loadMine(); if (view === "responses") loadResponses(); }, [view, loadMine, loadResponses]);

  const onActivated = () => { setIsPremium(true); setPremiumOpen(false); loadProjects(); };

  return (
    <div className="pp-root" data-testid="published-projects-page">
      <div className="pp-topbar">
        <div className="pp-tb-left">
          <div className="pp-tb-brand" onClick={() => navigate("/ipo-navigator")} data-testid="pp-brand">IPO<span>Navigator</span></div>
        </div>
        <div className="pp-tb-center">
          <button className="pp-tb-post" onClick={() => setShowPost(true)} data-testid="pp-post-project-top"><i className="ti ti-plus" /> Post a Project</button>
        </div>
        <div className="pp-tb-right">
          <button className="pp-tb-cta browse" onClick={() => navigate("/advisor-marketplace")} data-testid="pp-nav-marketplace"><i className="ti ti-users-group" /> <span className="pp-tb-cta-t">Advisor Marketplace</span></button>
          <button className="pp-tb-cta premium" onClick={() => setPremiumOpen(true)} data-testid="pp-nav-premium"><i className="ti ti-crown" /> <span className="pp-tb-cta-t">Become Premium</span></button>
        </div>
      </div>

      <div className="pp-wrap">
        <section className="pp-hero">
          <span className="pp-eyebrow"><i className="ti ti-briefcase" /> Open to all logged-in users</span>
          <h1 className="pp-h1">Published Projects for Advisors</h1>
          <p className="pp-lead">Companies post real IPO mandates here. Advisors browse and respond with their profile; owners choose who to engage. Full details unlock for Premium members.</p>
          <div className="pp-hero-cta">
            <button className="pp-btn" onClick={() => setShowPost(true)} data-testid="pp-post-project-hero"><i className="ti ti-plus" /> Post a Project</button>
            <button className="pp-btn ghost" onClick={() => navigate("/advisor-marketplace")} data-testid="pp-goto-marketplace"><i className="ti ti-arrow-right" /> Browse Advisors</button>
          </div>
        </section>

        {/* Premium rider — on both pages */}
        <div className="pp-rider" data-testid="pp-premium-rider">
          <div className="pp-rider-l">
            <div className="pp-rider-ic"><i className="ti ti-crown" /></div>
            <div>
              <div className="pp-rider-t">Become a Premium User to view the entire project details — including contact details</div>
              <div className="pp-rider-d">Non-premium members see only the poster's name and project scope. Upgrade to unmask budgets, timelines, full briefs and direct contact.</div>
            </div>
          </div>
          {isPremium ? (
            <span className="pp-premium-live" data-testid="pp-premium-active"><i className="ti ti-circle-check" /> Premium active</span>
          ) : (
            <button className="pp-btn" onClick={() => setPremiumOpen(true)} data-testid="pp-rider-upgrade"><i className="ti ti-brand-cashapp" /> Upgrade via Razorpay</button>
          )}
        </div>

        <div className="pp-tabs">
          <button className={`pp-tab ${view === "browse" ? "on" : ""}`} onClick={() => setView("browse")} data-testid="pp-tab-browse">Browse Projects</button>
          <button className={`pp-tab ${view === "mine" ? "on" : ""}`} onClick={() => setView("mine")} data-testid="pp-tab-mine">My Posted Projects</button>
          {isAdvisor && <button className={`pp-tab ${view === "responses" ? "on" : ""}`} onClick={() => setView("responses")} data-testid="pp-tab-responses">My Responses</button>}
        </div>

        {view === "browse" && (
          loading ? <div className="pp-spin" /> :
          projects.length === 0 ? <div className="pp-empty" data-testid="pp-empty">No projects posted yet. Be the first — <span className="pp-demo-link" onClick={() => setShowPost(true)}>post a project</span>.</div> :
          <div className="pp-grid" data-testid="pp-grid">
            {projects.map((p) => (
              <ProjectCard key={p.project_id} p={p} isAdvisor={isAdvisor} onReply={setReplyFor} onBecomePremium={() => setPremiumOpen(true)} />
            ))}
          </div>
        )}

        {view === "mine" && (
          mine.length === 0 ? <div className="pp-empty">You haven't posted any projects yet. <span className="pp-demo-link" onClick={() => setShowPost(true)}>Post one now</span>.</div> :
          <div data-testid="pp-mine">
            {mine.map((p) => (
              <div className="pp-card" key={p.project_id} style={{ marginBottom: 16 }} data-testid={`pp-mine-${p.project_id}`}>
                <span className="pp-tag">{p.category}</span>
                <div className="pp-title">{p.title}</div>
                <div className="pp-teaser">{p.short_summary}</div>
                <div className="pp-detail">
                  {p.budget_range && <div className="pp-drow"><span className="k">Budget</span><span className="v">{p.budget_range}</span></div>}
                  {p.timeline && <div className="pp-drow"><span className="k">Timeline</span><span className="v">{p.timeline}</span></div>}
                </div>
                <div style={{ fontWeight: 700, fontSize: 13.5, marginTop: 6 }}>{(p.interests || []).length} advisor{(p.interests || []).length !== 1 ? "s" : ""} interested</div>
                {(p.interests || []).map((it) => (
                  <InterestRow key={it.interest_id} it={it} navigate={navigate} onReplied={loadMine} />
                ))}
              </div>
            ))}
          </div>
        )}

        {view === "responses" && (
          myResponses.length === 0 ? <div className="pp-empty">You haven't responded to any projects yet.</div> :
          <div data-testid="pp-responses">
            {myResponses.map((it) => (
              <div className="pp-card" key={it.interest_id} style={{ marginBottom: 14 }}>
                <span className="pp-tag">{it.project?.category || ""}</span>
                <div className="pp-title">{it.project?.title || "Project"}</div>
                <div className="pp-teaser">{it.project?.short_summary || ""}</div>
                <div className="pp-int-msg"><b>Your note:</b> {it.message}</div>
                {(it.owner_replies || []).length > 0 && (
                  <div className="pp-int-thread">
                    {(it.owner_replies || []).map((r, idx) => (
                      <div className="pp-int-reply" key={idx}><b>Owner:</b> {r.body}</div>
                    ))}
                  </div>
                )}
                {(it.owner_replies || []).length === 0 && <div className="pp-count">Waiting for the project owner to respond…</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      <PostModal open={showPost} onClose={() => setShowPost(false)} categories={categories} onPosted={() => { setShowPost(false); loadProjects(); if (view === "mine") loadMine(); }} />
      <ReplyModal project={replyFor} onClose={() => setReplyFor(null)} onReplied={() => { setReplyFor(null); loadProjects(); }} />
      <PremiumModal open={premiumOpen} onClose={() => setPremiumOpen(false)} me={me} onActivated={onActivated} />
    </div>
  );
};

/* Owner's row for one interested advisor + reply thread */
const InterestRow = ({ it, navigate, onReplied }) => {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const s = it.advisor_snapshot || {};
  const send = async () => {
    if (!reply.trim()) { toast.error("Type a reply first."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/project-board/interest/${it.interest_id}/reply`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: reply }),
      });
      if (r.ok) { toast.success("Reply sent to the advisor."); setReply(""); onReplied(); }
      else toast.error("Could not send reply.");
    } catch { toast.error("Could not send reply."); }
    setBusy(false);
  };
  return (
    <div className="pp-int" data-testid={`pp-interest-${it.interest_id}`}>
      <div className="pp-int-head">
        <div className="pp-av">{initials(s.full_name)}</div>
        <div style={{ flex: 1 }}>
          <div className="pp-int-name">{s.full_name}
            {s.is_verified && <span className="pp-verify" style={{ marginLeft: 8 }}><i className="ti ti-rosette-discount-check" /> Verified</span>}
            {s.is_premium && <span className="pp-crown" style={{ marginLeft: 8 }}><i className="ti ti-crown" /> Premium</span>}
          </div>
          <div className="pp-int-meta">{s.city || "India"} · {s.ipo_experience ? `${s.years_of_experience}+ yrs IPO experience` : "New advisor"}</div>
        </div>
        <button className="pp-btn ghost sm" onClick={() => navigate(s.profile_url)} data-testid={`pp-view-advisor-${it.interest_id}`}>View full profile</button>
      </div>
      <div className="pp-chips">{(s.expertise_labels || []).map((l) => <span className="pp-chip" key={l}>{l}</span>)}</div>
      <div className="pp-int-msg">{it.message}</div>
      {(it.owner_replies || []).map((r, idx) => <div className="pp-int-reply" key={idx}><b>You:</b> {r.body}</div>)}
      <div className="pp-int-replybox">
        <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to this advisor…" data-testid={`pp-owner-reply-input-${it.interest_id}`} />
        <button className="pp-btn sm" disabled={busy} onClick={send} data-testid={`pp-owner-reply-send-${it.interest_id}`}>Send</button>
      </div>
    </div>
  );
};

export default PublishedProjects;
